import { Nullable } from '@dai/common'
import { Check } from '@mui/icons-material'
import {
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from '@mui/material'

import {
  PlanType,
  PriceTypeFragment,
  SubscriptionTypeFragment,
} from '@/graphql'
import { usePermissions } from '@/hooks/usePermissions'
import useT from '@/i18n/useT'
import { SubscriptionHelpers } from '@/utils/subscription.helpers'

const { VITE_APP__CONTACT_US_URL } = import.meta.env

type PlanPricingTileProps = {
  plan: SubscriptionTypeFragment
  selected: boolean
  currentPlanType: Nullable<PlanType>
  nextPlanType: Nullable<PlanType>
  handleSelectPlan: (plan: SubscriptionTypeFragment) => void
  viewOnly?: boolean
}

function PlanPricingTile({
  plan,
  selected,
  handleSelectPlan,
  currentPlanType,
  nextPlanType,
  viewOnly = false,
}: PlanPricingTileProps) {
  const { t } = useT()
  const theme = useTheme()
  const { permissions } = usePermissions()

  const isCurrentPlan =
    currentPlanType === plan.planType ||
    (plan.planType === PlanType.PREMIUM &&
      currentPlanType === PlanType.PREMIUM_FREE)

  return (
    <Paper
      sx={{
        height: '100%',
        mt: 1,
        borderRadius: 2,
        overflow: 'hidden',
        border: selected
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.secondary.main}`,
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 6,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box
          sx={{
            bgcolor: SubscriptionHelpers.isPlanType(plan, PlanType.PREMIUM)
              ? alpha(theme.palette.primary.main, 0.1)
              : 'background.paper',
            p: 3,
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              height: '85px',
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 1,
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h3" fontWeight="bold">
                {plan.name}
              </Typography>
              {isCurrentPlan ? (
                <Chip
                  label={t('pricingTable.currentPlan')}
                  color="primary"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                  }}
                />
              ) : nextPlanType === plan.planType ? (
                <Chip
                  label={t('pricingTable.nextPlan')}
                  color="primary"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                  }}
                />
              ) : (
                SubscriptionHelpers.isPlanType(plan, PlanType.PREMIUM) && (
                  <Chip
                    label={t('pricingTable.recommended')}
                    color="primary"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                    }}
                  />
                )
              )}
            </Box>

            <Typography variant="body1">{plan.description}</Typography>
          </Box>
          <Box sx={{ height: '95px' }}>
            {plan.prices.length > 0 &&
              !SubscriptionHelpers.isPlanType(plan, PlanType.ENTERPRISE) && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('pricingTable.pricing')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="h1" fontWeight="bold">
                        {isCurrentPlan &&
                        currentPlanType === PlanType.PREMIUM_FREE
                          ? '$0.00'
                          : `$${plan.prices[0].unitAmount}`}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        mt: 4,
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        {plan.prices[0].currency.toUpperCase()}{' '}
                        {t('pricingTable.pricing.perTimePerStore')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            {plan.prices.map((price: PriceTypeFragment) => (
              <Box key={price.id}>
                <Box>
                  {SubscriptionHelpers.isPlanType(plan, PlanType.ENTERPRISE) ? (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {permissions.CAN_MANAGE_BILLING ? (
                          <Link
                            href={VITE_APP__CONTACT_US_URL}
                            target="_blank"
                            underline="hover"
                            color="primary"
                            sx={{ cursor: 'pointer' }}
                          >
                            {t('pricingTable.contactUs')}
                          </Link>
                        ) : (
                          <></>
                        )}
                      </Typography>
                      <Typography variant="h2" fontWeight="bold">
                        {t('pricingTable.customPricing')}
                      </Typography>
                    </Box>
                  ) : (
                    <></>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
          <Box>
            {!viewOnly &&
              (!isCurrentPlan || nextPlanType) &&
              nextPlanType !== plan.planType &&
              (SubscriptionHelpers.isPlanType(plan, PlanType.ENTERPRISE) ? (
                <Button
                  variant="contained"
                  disabled={!permissions.CAN_MANAGE_BILLING}
                  color="primary"
                  fullWidth
                  size="large"
                  href={VITE_APP__CONTACT_US_URL}
                  target="_blank"
                  sx={{
                    py: 1,
                    borderRadius: '8px',
                  }}
                >
                  {t('pricingTable.contactUs')}
                </Button>
              ) : SubscriptionHelpers.isPlanType(plan, PlanType.BASIC) ? (
                <Button
                  variant="contained"
                  disabled={!permissions.CAN_MANAGE_BILLING}
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={() => {
                    handleSelectPlan(plan)
                  }}
                  sx={{
                    py: 1,
                    borderRadius: '8px',
                  }}
                >
                  {currentPlanType
                    ? t('pricingTable.switchToBasic')
                    : t('pricingTable.selectPlan')}
                </Button>
              ) : (
                <Button
                  variant={'contained'}
                  disabled={!permissions.CAN_MANAGE_BILLING}
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={() => {
                    handleSelectPlan(plan)
                  }}
                  sx={{
                    py: 1,
                    borderRadius: '8px',
                  }}
                >
                  {!permissions.CAN_MANAGE_BILLING
                    ? t('pricingTable.talkToYourAdmin')
                    : currentPlanType
                      ? nextPlanType
                        ? t('pricingTable.cancelDowngrade')
                        : t('pricingTable.upgradeToPremium')
                      : t('pricingTable.selectPlan')}
                </Button>
              ))}
          </Box>
        </Box>
        <Divider />
        <Box sx={{ p: 3, flexGrow: 1 }}>
          <List disablePadding>
            {plan.features.map((feature, idx) => (
              <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Check
                    sx={{ color: theme.palette.success.main }}
                    fontSize="small"
                  />
                </ListItemIcon>
                <ListItemText primary={feature.trim()} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    </Paper>
  )
}

export default PlanPricingTile
